import React from 'react';
import { X, AlertTriangle, TrendingUp, TrendingDown } from 'lucide-react';
import { AccountPosition } from '../types';

interface ClosePositionConfirmationModalProps {
  position: AccountPosition | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isClosing: boolean;
}

const ClosePositionConfirmationModal: React.FC<
  ClosePositionConfirmationModalProps
> = ({ position, isOpen, onClose, onConfirm, isClosing }) => {
  if (!isOpen || !position) return null;

  const formatCurrency = (value: number) => {
    if (isNaN(value)) return '$0.00';
    return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercentage = (value: number) => {
    if (isNaN(value)) return '0.00%';
    return `${value.toFixed(2)}%`;
  };

  // Get trade direction for icon
  const isBullish = position.tradeDirection === 'BULL';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Close Position
              </h2>
              <p className="text-sm text-gray-600">
                This action cannot be undone
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isClosing}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Position Summary */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-3 mb-3">
              {isBullish ? (
                <TrendingUp className="w-5 h-5 text-green-500" />
              ) : (
                <TrendingDown className="w-5 h-5 text-red-500" />
              )}
              <div>
                <h3 className="font-medium text-gray-900">
                  {position.symbol} - {position.tradeDirection}
                </h3>
                <p className="text-sm text-gray-600">
                  Status: {position.status}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Allocated Capital</p>
                <p className="font-medium text-gray-900">
                  {formatCurrency(position.allocatedCapital)}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Current Return</p>
                <p
                  className={`font-medium ${
                    position.actualReturn !== undefined &&
                    position.actualReturn >= 0
                      ? 'text-green-600'
                      : 'text-red-600'
                  }`}
                >
                  {position.actualReturn !== undefined
                    ? formatPercentage(position.actualReturn)
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Warning Message */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-red-800 mb-1">
                  Are you sure you want to close this position?
                </h4>
                <p className="text-sm text-red-700">
                  This will create a closing order to sell all holdings for this
                  position. The order will be submitted to your broker and
                  cannot be cancelled once placed.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              disabled={isClosing}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isClosing}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isClosing ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Closing...
                </div>
              ) : (
                'Close Position'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClosePositionConfirmationModal;
